import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { AbstractCommand } from '../AbstractCommand';

import type { VideoEditor } from '../../VideoEditor';

/**
 * Merges two clips by removing break that occur immediately after specified
 * transcript token id.
 * Semantics: provided token id is interpreted as the last token of a clip we
 * want to merge with the subsequent clip (if any).
 */
export class RemoveKaraokeBreakCommand extends AbstractCommand {
  private transcriptTokenId: string;
  private prevClips: KaraokeClip[] | null = null;

  constructor(editor: VideoEditor, transcriptTokenId: string) {
    super(editor);
    this.transcriptTokenId = transcriptTokenId;
  }

  execute(): boolean {
    if (!this.transcriptTokenId) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    // for undo
    this.prevClips = [...clips];

    const newClips: KaraokeClip[] = [];

    for (let i = 0; i < clips.length; i++) {
      const current = clips[i];
      // Check if the provided token id matches any token near the end of the clip.
      // segmentTokens() appends trailing punctuation (including spaces) to clips,
      // so the absolute last token may be whitespace while the caller provides
      // the last non-whitespace token id.
      if (this.isTokenNearEndOfClip(current) && i < clips.length - 1) {
        const next = clips[i + 1];
        const merged = this.mergeTwoClips(current, next);
        newClips.push(merged);
        i++; // skip next clip (consumed)
      } else {
        newClips.push(current);
      }
    }

    track.karaokeClips = newClips;

    this.editor.markDirty('karaoke_remove_breaks');
    return true;
  }

  /**
   * Check if the provided token id is the last token or the last non-whitespace
   * token in the clip. Clips produced by segmentTokens() may have trailing
   * space/punct tokens after the last meaningful token.
   */
  private isTokenNearEndOfClip(clip: KaraokeClip): boolean {
    const tokens = clip.transcriptTokens;
    for (let j = tokens.length - 1; j >= 0; j--) {
      const token = tokens[j];
      if (token.id === this.transcriptTokenId) {
        return true;
      }
      // Stop scanning once we hit a non-whitespace token that doesn't match
      if (!(token.type === 'punct' && token.value === ' ')) {
        return false;
      }
    }
    return false;
  }

  private mergeTwoClips(current: KaraokeClip, next: KaraokeClip) {
    return new KaraokeClip({
      id: current.id, // preserve id of first clip in merged pair
      transcriptTokens: [...current.transcriptTokens, ...next.transcriptTokens],
      hiddenTranscriptTokenIds: [
        ...current.hiddenTranscriptTokenIds,
        ...next.hiddenTranscriptTokenIds.filter(
          id => !current.hiddenTranscriptTokenIds.includes(id),
        ),
      ],
      ignoredByFiltersIds: [
        ...current.ignoredByFiltersIds,
        ...next.ignoredByFiltersIds.filter(
          id => !current.ignoredByFiltersIds.includes(id),
        ),
      ],
      startSecondsOffset: current.startSecondsOffset,
      endSecondsOffset: next.endSecondsOffset,
      karaokeConfig: current.karaokeConfig,
      track: current.track,
      softBreakAfterTokenIds: [
        ...(current.softBreakAfterTokenIds ?? []),
        ...(next.softBreakAfterTokenIds ?? []),
      ],
    });
  }

  undo(): boolean {
    if (!this.prevClips) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    track.karaokeClips = this.prevClips;

    this.editor.markDirty('karaoke_remove_breaks_undo');
    return true;
  }

  getName(): string {
    return 'RemoveKaraokeBreakCommand';
  }

  getDescription(): string {
    return 'Merges karaoke clips by removing specified breaks';
  }
}
